//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region IMR Imports

import { IMRBaseComponentExtension } from '@imr/controls.angularjs/components/base/extension'

//#endregion

import { DataTypeGroupComponent, ConvertHelper, TypeHelper } from '../../../../helpers'

import { IOpObject, IOpData } from '../../../../models/util/interfaces'
import { Enums } from '../../../../models/common'
import { OpDataTypeInGroup } from '../../../../models/core'

import { DataTypeFormatService, DATA_TYPE_FORMAT_SERVICE_NAME, DataTypeFormatter } from '../../../../services/data-type'
import { UserSessionService } from '../../../../services/user-session'

import { IIMRParemetersExtension } from "../parameters.interfaces"

import { BasicParameter } from './basic.parameters.extension'


// Public Types

export interface IDataTypeParametersExtension {
    setObject(object: IOpObject | { dynamicValues: { Key: string, Value: any }[] }): void
}

export type BasicParametersExtensionOptions = {
    dataType: {
        dataTypeGroupCategory?: Enums.SimaxDataTypeGroupCategory
        dataTypes?: OpDataTypeInGroup[]
        moduleName?: Enums.SimaxModuleName
    }

    object?: IOpObject | { dynamicValues: { Key: string, Value: any }[] }
    useDynamicValues?: boolean
}

// Public Types End


export class DataTypeParametersExtension extends IMRBaseComponentExtension implements IIMRParemetersExtension, IDataTypeParametersExtension {

    // Members

    private object: IOpObject | { dynamicValues: { Key: string, Value: any }[] }
    private dataTypes: OpDataTypeInGroup[]

    private userSession: UserSessionService
    private dataTypeFormatService: DataTypeFormatService

    private dataTypeFormatter: DataTypeFormatter
    private parameters: BasicParameter[]
    private isUseDynamicValues: boolean

    // Members End


    // Ctor

    constructor(private readonly options: BasicParametersExtensionOptions) {
        super()

        this.setDefaults()
        this.setOptions()
    }

    // Ctor End


    // IIMRParametersExtension Implementation

    public initialize(userSession: UserSessionService): JQueryPromise<void> {
        this.userSession = userSession
        this.dataTypeFormatService = super.getService<DataTypeFormatService>(DATA_TYPE_FORMAT_SERVICE_NAME)

        return this.refresh()
    }

    public getPrameters(): BasicParameter[] {
        return this.parameters
    }

    public refresh(): JQueryPromise<void> {
        return this
            .getDataTypes()
            .then((dataTypes: OpDataTypeInGroup[]) => {
                return this
                    .getDataTypeFormatter(dataTypes)
                    .then((dataTypeFormatter: DataTypeFormatter) => {
                        this.dataTypeFormatter = dataTypeFormatter

                        const sortedDataTypes: OpDataTypeInGroup[] = _.sortBy(dataTypes, (dataType: OpDataTypeInGroup) => dataType.orderNBR)

                        if (this.object == null) {
                            this.parameters = this.getParametersFromDataTypeInGroup(sortedDataTypes)
                        }
                        else {
                            if (this.isUseDynamicValues) {
                                return this.getParmetersFromDynamicValues(sortedDataTypes, this.object)
                                    .then((paramters: BasicParameter[]) => {
                                        this.parameters = paramters
                                    })
                            }

                            this.parameters = this.getParametersFromIOpObject(sortedDataTypes, this.object as IOpObject)
                        }

                    })
            })
    }

    // IIMRParametersExtension Implementation End


    // IDataTypeParametersExtension Implementation

    public setObject(object: IOpObject | { dynamicValues: { Key: string, Value: any }[] }): void {
        this.object = object
    }

    // IDataTypeParametersExtension Implementation End


    // Options

    private setDefaults(): void {
        this.isUseDynamicValues = false
        this.object = null
        this.parameters = []
    }

    private setOptions(): void {
        if (this.options != null) {
            if (this.options.useDynamicValues != null) {
                this.isUseDynamicValues = this.options.useDynamicValues
            }

            if (this.options.object != null) {
                this.object = this.options.object
            }
        }
    }

    // Options End


    // DataTypes

    private getParametersFromDataTypeInGroup(dataTypes: OpDataTypeInGroup[]): BasicParameter[] {
        return _.map(dataTypes, (dataTypeInGroup: OpDataTypeInGroup) => {
            return {
                caption: dataTypeInGroup.toString(),
            }
        })
    }

    private getDataTypes(): JQueryPromise<OpDataTypeInGroup[]> {
        if (this.options != null) {
            if (this.options.dataType != null) {
                if (this.options.dataType.dataTypeGroupCategory != null && this.options.dataType.moduleName != null) {
                    return DataTypeGroupComponent.getDataTypeInGroupByModule(this.userSession.getDataProvider(), this.options.dataType.dataTypeGroupCategory, this.options.dataType.moduleName)
                }

                if (_.isArray(this.options.dataType.dataTypes)) {
                    return $.Deferred<OpDataTypeInGroup[]>().resolve(this.options.dataType.dataTypes).promise()
                }
            }
        }

        return $.Deferred<OpDataTypeInGroup[]>().resolve([]).promise()
    }

    private getDataTypeFormatter(dataTypes: ReadonlyArray<OpDataTypeInGroup>): JQueryPromise<DataTypeFormatter> {
        const idDataTypes: number[] = _.map(dataTypes, (dataType: OpDataTypeInGroup) => {
            return dataType.idDataType
        })

        return this.dataTypeFormatService.getDataTypeFormatter(idDataTypes)
    }

    // DataTypes End


    // IOpObject

    private getParametersFromIOpObject(dataTypes: OpDataTypeInGroup[], object: IOpObject): BasicParameter[] {
        if (_.isArray(dataTypes)) {
            const parameters: BasicParameter[] = []

            dataTypes.forEach((dataTypeInGroup: OpDataTypeInGroup) => {
                object.dataList
                    .toArray()
                    .forEach((dataFromDynamicValue: IOpData) => {
                        if (dataTypeInGroup.idDataType === dataFromDynamicValue.idDataType) {
                            parameters.push(this.prepareParameter(dataTypeInGroup, dataFromDynamicValue))
                        }
                    })
            })

            return parameters
        }

        return []
    }

    // IOpObject End


    // Dynamic Values

    private getParmetersFromDynamicValues(dataTypes: OpDataTypeInGroup[], object: { dynamicValues: { Key: string, Value: any }[] }): JQueryPromise<BasicParameter[]> {
        if (_.isArray(dataTypes)) {
            return $
                .when(...object.dynamicValues.map(dynamicValue => ConvertHelper.dynamicValueToIOpData(this.userSession.getDataProvider(), dynamicValue)))
                .then((...dataFromDynamicValueList: IOpData[]) => {
                    const parameters: BasicParameter[] = []

                    dataTypes.forEach((dataTypeInGroup: OpDataTypeInGroup) => {
                        dataFromDynamicValueList.forEach((dataFromDynamicValue: IOpData) => {
                            if (dataTypeInGroup.idDataType === dataFromDynamicValue.idDataType) {
                                parameters.push(this.prepareParameter(dataTypeInGroup, dataFromDynamicValue))
                            }
                        })
                    })

                    return parameters
                })
        }

        return $.Deferred<BasicParameter[]>().resolve([]).promise()
    }

    // Dynamic Values End


    // Paramters

    private prepareParameter(dataTypeInGroup: OpDataTypeInGroup, data: IOpData): BasicParameter {
        let unitTest: string = TypeHelper.emptyString

        if (data.dataType != null && data.dataType.unit != null) {
            unitTest = data.dataType.unit.toString()
        }

        const parameter: BasicParameter = {
            caption: dataTypeInGroup.toString(),
            isCheckBox: false,
            unit: unitTest,
            value: data.value
        }

        if (dataTypeInGroup.dataType != null && dataTypeInGroup.dataType.idDataTypeClass === Enums.DataTypeClass.Boolean) {
            parameter.isCheckBox = true
        }
        else {
            parameter.value = this.prepareValue(data.value, dataTypeInGroup)
        }

        return parameter
    }

    private prepareValue(value: any, dataTypeInGroup: OpDataTypeInGroup): any {
        return this.dataTypeFormatter.formatDisplayValue(value, {
            dataType: dataTypeInGroup.dataType,
        })
    }

    // Parameters End
}
